/**
 * Modern Color Palette for PACS Application
 * Keeps existing brand colors while adding sophisticated neutral scale
 */

// Existing brand colors (preserved)
const PRIMARY = {
  50: '#F6FCFF',
  100: '#E6F7FF',
  200: '#BAE7FF',
  300: '#91D5FF',
  400: '#69CBF9',  // Original main
  500: '#40A9FF',
  600: '#1890FF',
  700: '#096DD9',
  800: '#0050B3',
  900: '#052F5D',  // Original dark
  main: '#69CBF9',
  light: '#F6FCFF',
  dark: '#052F5D',
  contrastText: '#fff',
};

// Modern neutral scale (sophisticated grays)
const NEUTRAL = {
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#E5E5E5',
  300: '#D4D4D4',
  400: '#A3A3A3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
};

// Accent color for CTAs (existing button color)
const ACCENT = {
  main: '#26BAFF',
  light: '#5CCFFF',
  dark: '#0099DD',
  contrastText: '#fff',
};

// Semantic status colors (modern palette)
const STATUS = {
  success: {
    main: '#10B981',
    light: '#D1FAE5',
    dark: '#059669',
    contrastText: '#fff',
  },
  warning: {
    main: '#F59E0B',
    light: '#FEF3C7',
    dark: '#D97706',
    contrastText: '#fff',
  },
  error: {
    main: '#EF4444',
    light: '#FEE2E2',
    dark: '#DC2626',
    contrastText: '#fff',
  },
  info: {
    main: '#3B82F6',
    light: '#DBEAFE',
    dark: '#2563EB',
    contrastText: '#fff',
  },
};

// Medical-specific status colors
const MEDICAL_STATUS = {
  requested: '#8B5CF6',    // Violet
  scheduled: '#F59E0B',    // Amber
  inProgress: '#3B82F6',   // Blue
  complete: '#10B981',     // Emerald
  missed: '#EF4444',       // Red
  pending: '#6B7280',      // Gray
  cancelled: '#9CA3AF',    // Light gray
};

// Light theme palette - Modern Admin Panel with Light Blue Tones
export const lightPalette = {
  mode: 'light',
  primary: PRIMARY,
  secondary: {
    main: PRIMARY[100],
    dark: PRIMARY[200],
    light: '#ffffff',
    contrastText: PRIMARY.dark,
  },
  accent: ACCENT,
  neutral: NEUTRAL,
  success: STATUS.success,
  warning: STATUS.warning,
  error: STATUS.error,
  info: STATUS.info,
  medicalStatus: MEDICAL_STATUS,
  background: {
    default: '#EBF5FB',           // Light blue-tinted background
    paper: '#ffffff',
    elevated: '#ffffff',
    subtle: PRIMARY[50],          // Very light blue for subtle backgrounds
    sidebar: PRIMARY.dark,        // Dark blue sidebar
    card: '#ffffff',
    gradient: 'linear-gradient(135deg, #E8F4FD 0%, #F0F7FC 100%)',
  },
  text: {
    primary: NEUTRAL[800],
    secondary: NEUTRAL[500],
    disabled: NEUTRAL[400],
    hint: NEUTRAL[400],
  },
  divider: 'rgba(5, 47, 93, 0.08)',  // Subtle blue-tinted divider
  action: {
    active: NEUTRAL[600],
    hover: 'rgba(105, 203, 249, 0.08)',
    selected: 'rgba(105, 203, 249, 0.15)',
    disabled: NEUTRAL[300],
    disabledBackground: PRIMARY[50],
  },
  // Legacy support
  btncolor: { main: ACCENT.main },
  inputbg: { main: PRIMARY[50] },
};

// Dark theme palette (for imaging pages)
export const darkPalette = {
  mode: 'dark',
  primary: {
    ...PRIMARY,
    main: '#69CBF9',
    light: '#91D5FF',
    dark: '#40A9FF',
  },
  secondary: {
    main: NEUTRAL[800],
    dark: NEUTRAL[900],
    light: NEUTRAL[700],
    contrastText: '#ffffff',
  },
  accent: ACCENT,
  neutral: NEUTRAL,
  success: STATUS.success,
  warning: STATUS.warning,
  error: STATUS.error,
  info: STATUS.info,
  medicalStatus: MEDICAL_STATUS,
  background: {
    default: '#0A0A0A',
    paper: NEUTRAL[900],
    elevated: NEUTRAL[800],
    subtle: NEUTRAL[800],
  },
  text: {
    primary: NEUTRAL[50],
    secondary: NEUTRAL[400],
    disabled: NEUTRAL[600],
    hint: NEUTRAL[500],
  },
  divider: NEUTRAL[700],
  action: {
    active: NEUTRAL[400],
    hover: 'rgba(255, 255, 255, 0.08)',
    selected: 'rgba(105, 203, 249, 0.16)',
    disabled: NEUTRAL[700],
    disabledBackground: NEUTRAL[800],
  },
  // Legacy support
  btncolor: { main: ACCENT.main },
  inputbg: { main: NEUTRAL[800] },
};

// Default export for backwards compatibility
const palette = {
  ...lightPalette,
  // Additional legacy properties
  text: {
    ...lightPalette.text,
    titleColor: NEUTRAL[700],
  },
};

export default palette;
